import React, { useEffect, useContext, useState, useRef } from 'react';
import { useQuery, useMutation, useCallback } from '@apollo/client';

import { Context } from '@store/store';
import { GET_BOOK_DATA } from '@graphql/main';

import styles from './app.module.scss';
import { useCookies } from 'react-cookie';

import Spinner from './components/spinner';
import ControlBar from './components/control-bar';
import EditorBar from './components/editor-bar';

import EditorNewDoc from './components/editor-sidebars/editor-new-doc';
import EditorOpenDoc from './components/editor-sidebars/editor-open-doc';
import EditorComments from './components/editor-sidebars/editor-comments';
import EditorSource from './components/editor-sidebars/editor-source';
import EditorCookie from './components/editor-cookie';

import TOCSidebar from './components/sidebars/toc-sidebar';
import ThumbnailsSidebar from './components/sidebars/thumbnails-sidebar';
import BookmarksSidebar from './components/sidebars/bookmarks-sidebar';
import PageContainer from './components/page-container';
import Footer from './components/footer';
import LoginModal from './components/modals/login-modal';
import ArrowButtons from './components/arrow-buttons';
import ZoomButtons from './components/zoom-buttons';
import BetaButton from './components/beta/beta-button';
import BetaModal from './components/beta/beta-modal';
import EditorModal from './components/modals/editor-modal';
import ShelfModal from './components/modals/shelf-modal';
import BookmarksModal from './components/modals/bookmarks-modal';
import SearchInput from './components/search/search-input';
import SettingsDropdown from './components/settings-dropdown';
import SpeechDropdown from './components/speech-dropdown';

import {
  StyledContainer,
  EditorBody,
  EditorPortrait,
  EditorPortraitBody,
  EditorPortraitText,
  EditorPortraitSubText,
  EditorPortraitIcon,
} from './container.styles';

import makePairsInPagesArray from '@utils/makePairsInPagesArray';

const Main = ({ bookId }) => {
  const queryParameters = new URLSearchParams(window.location.search)
  const notesId = queryParameters.get("notesId")

  const [state, dispatch] = useContext(Context);
  const [preloading, setPreloading] = useState(true);
  const [audience, setAudience] = useState(null);
  const [postId, setPostId] = useState(null);
  const [value, setValue] = useState([])
  const [shortBibl, setShortBibl] = useState(null)

  const handleChange = (value) => {
    setValue(value)
  }

  useEffect(() => {
    const arr = value.map(([name, value]) => ({ id: name, shortBibl: null }));
    setShortBibl(arr);
  }, [value]);

  const handleSourceChange = (shortBibls, id) => {
    if (value.length > 0 && shortBibl.length > 0) {
      const myNextList = [...shortBibl];
      const short = myNextList.find(
        a => a.id === id
      );
      short.shortBibl = shortBibls;
      setShortBibl(myNextList);
    }
  }

  const { loading, error, data } = useQuery(GET_BOOK_DATA, {
    variables: { id: bookId },
  });

  const [cookies, setCookies] = useCookies(['agreement']);
  const [cookie, setCookie] = useState(cookies.agreement);

  function setCookieFunc() {
    setCookies('agreement', true, { path: '/' });
    setCookie(true);
  }

  useEffect(() => {
    setAudience(data?.me);

    if (data && data.book) {
      if (notesId) {
        dispatch({ type: 'TOGGLE_EDITOR_MODAL' });
        dispatch({ type: 'TOGGLE_EDITOR_NEW_DOC_MODAL', edit_doc: notesId });
        queryParameters.delete("notesId");
      }

      document.title = `${data.book.title}  |  Электронная библиотека Национальной библиотеки РС(Я)`;
      dispatch({
        type: 'SET_IS_USER_LOGGED_IN',
        payload: !!data?.me,
      });
      dispatch({
        type: 'SET_IS_USER_TARGET_AUDIENCE',
        payload: data?.me,
      });
      dispatch({
        type: 'SET_IS_ON_SHELF',
        payload: data.book.is_on_shelf,
      });
      dispatch({
        type: 'SET_PAGES_COUNT',
        payload: data.book.docviewer_htmls_urls.length,
      });
      if (data.book.last_read_page !== null) {
        dispatch({
          type: 'SET_CURRENT_PAGE',
          payload: data.book.last_read_page,
        });
      }
      const initialPage = parseInt((new URLSearchParams(window.location.search)).get('initial-page'));
      if (initialPage > 0) {
        dispatch({
          type: 'SET_CURRENT_PAGE',
          payload: initialPage - 1,
        });
      }
      const pairsPagesHtmlUrls = makePairsInPagesArray(data.book.docviewer_htmls_urls)
      dispatch({
        type: 'SET_PAIRS_PAGES_URLS',
        payload: pairsPagesHtmlUrls
      })
      dispatch({
        type: 'SET_BOOK_IS_ELIGIBLE_FOR_AI_TOOLS',
        payload: data.book.langs.map((x) => x.id).filter(x => ["26"].includes(x)).length > 0,
      });
      setPreloading(false)
    }
  }, [data]);

  useEffect(() => {
    if (data?.book?.access_for_current_user != 'allowed') {
      return; 
    }

    const script = document.createElement('script');
    const currentTimestamp = new Date().getTime();
    script.src = `https://old.nlrs.ru/elib-book-chat/dist/script.js?ts=${currentTimestamp}`;
    script.async = true;
    script.setAttribute('data-id', 'nlrs-chat-with-book-script');
    script.setAttribute('data-elib-book-id', data.book.id);

    script.onload = () => {
      if (window.elibBookChat && typeof window.elibBookChat.renderWidget === 'function') {
        window.elibBookChat.renderWidget('#nlrs-chat-with-book-container');
      }
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [data]);

  if (preloading) return <Spinner />;
  if (error)
    return (
      <p>
        Ошибка. Пожалуйста перезагрузите вкладку или проверьте ваше
        интернет-соединение.
      </p>
    );
  if (!data.book)
    return <p>Электронного документа с ID {bookId} не существует.</p>;

  return (
    <div className={styles.root}>
      <ControlBar
        cookie={cookie}
        bookId={bookId}
        bookTitle={data.book.title}
        bookAuthors={data.book.authors.map(t => t.full_name).join(', ')}
        bookBibl={data.book.shortBibl}
        targetAudience={audience?.target_audience_id}
        userId={audience?.uid}
      />
      <TOCSidebar
        toc={data.book.outline}
        bookTitle={data.book.title}
        initials={data.book.authors.map(t => t.full_name).join(', ')}
      />
      <ThumbnailsSidebar thumbnails={data.book.docviewer_thumbnails_urls} />
      <BookmarksSidebar />
      <SearchInput
        searchIndexUrl={data.book.search_index_url}
        textByPagesUrl={data.book.text_by_pages_url}
      />

      {["48059", "42050", "175723", "40865", "66221", "13679", "89060", "65075", '12868'].includes(audience?.uid) || ["2","4","5","6","9","10","11","12","13","14"].includes(audience?.target_audience_id) ?
          <>
          <EditorBody>
            <EditorBar value={value} />   
            <EditorOpenDoc bookId={bookId} />
            <EditorNewDoc onChange={handleChange} />
            <EditorSource onChange={handleSourceChange} id={bookId} value={value} />
            <EditorComments />
            <EditorCookie cookie={setCookieFunc} />
          </EditorBody>
          {state.isEditorSidebarOpened &&
          <EditorPortrait>
            <EditorPortraitBody>
              <EditorPortraitText>
                Режим конспектирования<br/> включен
              </EditorPortraitText>
              <EditorPortraitIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="79" height="78" viewBox="0 0 79 78" fill="none">
                  <g filter="url(#filter0_d_2093_18010)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M31.6601 67.5581C35.5966 67.5581 38.7636 64.4326 45.1009 58.1879L63.0196 40.5219C69.3569 34.2739 72.5239 31.1516 72.5239 27.2707C72.5239 23.3898 69.3536 20.2674 63.0196 14.0195L60.7806 11.8121C54.4466 5.56419 51.2763 2.44186 47.3399 2.44186C43.4034 2.44186 40.2364 5.56744 33.8991 11.8121L15.9804 29.4781C9.64306 35.726 6.47607 38.8484 6.47607 42.7293C6.47607 46.6102 9.64636 49.7326 15.9804 55.9805L18.2194 58.1879C24.5567 64.4358 27.7237 67.5581 31.6601 67.5581ZM19.9729 43.2112C19.5034 42.7798 18.8824 42.545 18.2408 42.5562C17.5991 42.5673 16.9869 42.8236 16.5331 43.271C16.0793 43.7184 15.8194 44.322 15.8081 44.9546C15.7967 45.5872 16.0349 46.1994 16.4724 46.6623L27.6675 57.706C28.1317 58.1646 28.7617 58.4225 29.4188 58.4231C29.7441 58.4234 30.0664 58.3605 30.3671 58.238C30.6678 58.1156 30.9411 57.9359 31.1714 57.7093C31.4016 57.4827 31.5844 57.2136 31.7092 56.9174C31.834 56.6211 31.8984 56.3035 31.8987 55.9828C31.8993 55.3349 31.6389 54.7134 31.1747 54.2549L19.9729 43.2112ZM55.1831 19.5414C54.7482 19.0788 54.2232 18.7075 53.6394 18.4497C53.0556 18.1919 52.4251 18.0528 51.7855 18.0408C51.1459 18.0289 50.5105 18.1442 49.9172 18.38C49.3239 18.6158 48.785 18.9672 48.3326 19.4132C47.8803 19.8591 47.5239 20.3905 47.2847 20.9754C47.0455 21.5603 46.9285 22.1868 46.9407 22.8173C46.9528 23.4479 47.0939 24.0696 47.3554 24.6451C47.6169 25.2207 47.9935 25.7383 48.4627 26.167C49.3648 26.9913 50.5558 27.4389 51.7855 27.4159C53.0153 27.3929 54.1881 26.901 55.0579 26.0435C55.9276 25.1861 56.4265 24.0297 56.4498 22.8173C56.4732 21.6049 56.0191 20.4308 55.1831 19.5414ZM6.05996 21.1302C5.73921 21.0762 5.4324 20.9605 5.15705 20.7895C4.8817 20.6186 4.64321 20.3958 4.45521 20.134C4.26721 19.8722 4.13339 19.5764 4.06139 19.2635C3.98939 18.9507 3.98063 18.6269 4.0356 18.3107C5.8321 7.8921 15.1316 4.81029e-07 26.2937 0C26.743 0.000808229 27.1836 0.122068 27.5683 0.350801C27.953 0.579534 28.2674 0.907126 28.4777 1.29849C28.6881 1.68986 28.7865 2.13026 28.7625 2.57255C28.7384 3.01484 28.5928 3.44235 28.3412 3.8093L24.9926 8.69302C24.8149 8.96863 24.583 9.2063 24.3106 9.39199C24.0381 9.57767 23.7307 9.7076 23.4064 9.77408C23.0821 9.84056 22.7476 9.84224 22.4227 9.77903C22.0977 9.71581 21.7889 9.58898 21.5146 9.40605C21.2402 9.22311 21.0059 8.98778 20.8254 8.71398C20.6449 8.44018 20.522 8.13347 20.4638 7.812C20.4057 7.49053 20.4135 7.16083 20.4869 6.8424C20.5603 6.52397 20.6977 6.22329 20.891 5.95814L21.1123 5.63256C14.8047 7.51116 10.0228 12.7302 8.91653 19.1312C8.8059 19.7691 8.44288 20.3377 7.90724 20.712C7.37161 21.0862 6.70718 21.2387 6.05996 21.1302ZM72.94 48.8698C73.2608 48.9238 73.5676 49.0395 73.8429 49.2105C74.1183 49.3814 74.3568 49.6042 74.5448 49.866C74.7328 50.1278 74.8666 50.4236 74.9386 50.7365C75.0106 51.0493 75.0194 51.3731 74.9644 51.6893C73.1679 62.1079 63.8717 70 52.7096 70C52.26 69.9998 51.819 69.8789 51.4339 69.6505C51.0487 69.422 50.7338 69.0944 50.5231 68.7029C50.3123 68.3115 50.2136 67.8708 50.2375 67.4283C50.2614 66.9857 50.407 66.5579 50.6588 66.1907L54.0074 61.307C54.3837 60.7909 54.9492 60.4398 55.5838 60.3283C56.2184 60.2167 56.8722 60.3535 57.4061 60.7095C57.9401 61.0656 58.3122 61.6128 58.4435 62.2351C58.5747 62.8573 58.4547 63.5055 58.109 64.0419L57.8877 64.3674C64.1953 62.4888 68.9772 57.2698 70.0835 50.8688C70.1941 50.2308 70.5571 49.6623 71.0928 49.288C71.6284 48.9138 72.2928 48.7613 72.94 48.8698Z" fill="white"/>
                  </g>
                  <defs>
                    <filter id="filter0_d_2093_18010" x="0" y="0" width="79" height="78" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dy="4"/>
                      <feGaussianBlur stdDeviation="2"/>
                      <feComposite in2="hardAlpha" operator="out"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2093_18010"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2093_18010" result="shape"/>
                    </filter>
                  </defs>
                </svg>
              </EditorPortraitIcon>
              <EditorPortraitSubText>
                Переверните устройство<br/> горизонтально
              </EditorPortraitSubText>
            </EditorPortraitBody>
          </EditorPortrait>}
          </>
        : null
      } 

      <StyledContainer 
        state={state}
      >
        <SettingsDropdown bookId={bookId} />
        {['8888', '12809', '120169'].includes(data.book.id) && <SpeechDropdown
          bookId={bookId}
          editorButtonIsPresent={
            (state.isUserLoggedIn && ["48059", "42050", "175723", "40865", "66221", "13679", "89060", "65075"].includes(audience?.uid))
            || (state.isUserLoggedIn && ["2", "4", "5", "6", "9","11","12","13"].includes(audience?.target_audience_id))
          }
        />}
        <PageContainer
          pages={data.book.docviewer_htmls_urls}
          bookId={bookId} 
          userId={audience?.uid}
          audience={audience}
          bookTitle={data.book.title}
          bookAuthors={data.book.authors.map(t => t.full_name).join(', ')}
          initials={data.book.authors.map(t => t.initials).join(', ')}
          pageCount={data.book.innerPagesCount}
          publisher={data.book.publisher}
          pubDate={data.book.pubDate}
        />
        <Footer pagesCount={data.book.docviewer_htmls_urls.length} />
        <LoginModal />
        <ArrowButtons />
        <ZoomButtons />
      </StyledContainer>
      <ShelfModal />
      <EditorModal />
      <BookmarksModal />
      <div id="nlrs-chat-with-book-container"></div>
    </div>
  );
};

export default Main;
